import { ERC725YDataKeys } from '@lukso/lsp-smart-contracts'
import BN from 'bn.js'

import { EventData } from '@/shared/models/ContractEvent'
import {
  Profile,
  UpAddress,
  EoaAddress,
  ContractAddress,
} from '@/shared/models/Profile'
import { Origin } from '@/shared/models/Origin'
import { GasProvider } from '@/shared/models/GasProvider'
import { DetailedError, ErrorMessage } from './DetailedError'
import { QuotaDTO } from '@/business-logic/relay-service'

export type Transaction = {
  from: UpAddress
  createdBy: Profile['address']
  requestId: string
  createdAt?: number
  to?: UpAddress | EoaAddress | ContractAddress
  value?: string
  data?: string
  nonce?: string
  transactionHash?: string
  signature?: string | null
  gasUsed?: number
  cumulativeGasUsed?: number
  gasPrice?: string
  gasLimit?: string
  transactionFee?: string
  /// Base fee per gas
  baseFee?: number
  maxFeePerGas?: string
  maxPriorityFeePerGas?: string
  priorityFee?: number // This field is not used anywhere
  gasUsedPercent?: number
  blockNumber?: number
  transactionIndex?: number
  validityTimestamps?: string
  origin?: Origin['url']

  contractEvents?: EventData[]
  components?: TransactionComponent[]
  setDataComponents?: SetDataSelector[]
  error?: DetailedError | ErrorMessage
  status: TransactionStatus
  gasProvider?: GasProvider
  force?: boolean
  options?: TransactionOptions
}

export type TransactionChangeMessage = {
  key: keyof Transaction
  value?: any
}

export type TransactionOptions = TransactionChangeMessage[]

export type SendTransactionInput = Partial<
  Pick<
    Transaction,
    | 'from'
    | 'to'
    | 'gasLimit'
    | 'maxPriorityFeePerGas'
    | 'gasPrice'
    | 'value'
    | 'data'
  >
> & {
  gasLimit?: string
  nonce?: string
  chainId?: string | number
}

export type EOATransaction = Transaction &
  Required<
    Pick<
      Transaction,
      'data' | 'gasLimit' | 'maxPriorityFeePerGas' | 'maxFeePerGas' | 'gasPrice'
    >
  >

export type RelayerTransaction = Transaction &
  Required<Pick<Transaction, 'data' | 'gasProvider'>>

export type TransactionIntermediateData = {
  estimateGasLimit?: string

  // Capture initial transaction so we can replay it for setup
  // after force flag changes and we need to prepare a new promise
  initialData?: string
  /**
   * Recommended max priority fee per gas received from the network.
   */
  networkMaxPriorityFeePerGas?: string
  controllerAddress?: EoaAddress
  /**
   * If undefined or @see ZERO_ADDRESS and has data then treated as smart contract deployment.
   */
  to?: string
  from: string
  value?: string
  /**
   * If the ERC725X.execute call was decoded we store the operation type to reuse it later during encoding
   * as operation type value can be something else but CALL, CREATE, CREATE2, STATICCALL or DELEGATECALL.
   */
  executeCallOperationType?: BN
  generateExecuteAbi?: boolean

  /**
   * Tells us if the keyring was unlocked at the moment of preparation of this transaction data. As transaction requests
   * may come when extension is locked and hidded we may experience issues with calcuation of relay service quota for
   * a profile. Recalculation must be re-attempted after the keyring is unlocked.
   * If set to `true` - no recalculation is required.
   */
  isKeyringUnlocked?: boolean
} & Pick<
  Transaction,
  | 'components'
  | 'data'
  | 'gasProvider'
  | 'maxPriorityFeePerGas'
  | 'gasLimit'
  | 'gasPrice'
  | 'baseFee'
  | 'maxFeePerGas'
  | 'error'
> & {
    relayerQuota?: QuotaDTO
  }

export type SendTransactionOutput = Omit<SendTransactionInput, 'chainId'>

export type TransactionStatus =
  | 'INITIALIZED'
  | 'PENDING'
  | 'COMPLETED'
  | 'FAILED'

type TypeValue = {
  value?: string | boolean
  type: string
  isNegation?: boolean
}

export type TransactionComponentConditionCall = {
  type: 'call'
  functionSignature: string
  inputs?: TypeValue[]
  outputs: TypeValue[] // Conditions without outputs don't make sense
}

export type TransactionComponentCondition = TransactionComponentConditionCall

export type TransactionConditionCalls = {
  call: (args: TransactionComponentConditionCall) => Promise<boolean>
}

export type TransactionComponent = {
  component: string
  functionSignature?: string
  standard?: string
  legacyWarning?: string
  selector?: string
  legacy?: boolean
}

export interface InternalTransactionComponent extends TransactionComponent {
  conditions?: TransactionComponentCondition[]
}

export type SetDataSelector = {
  name: string
  key: string
  keyType: string
  valueContent: string
  valueType: string
  component: string
}

export type SetDataKeyValuePair = {
  setDataKey: string
  setDataValue: string
}

export enum SetDataFunctionSignatures {
  BATCH = 'setDataBatch(bytes32[],bytes[])',
  SINGLE = 'setData(bytes32,bytes)',
}

export const SET_DATA_COMPONENT_NAME = 'SetData'
export const SET_DATA_BATCH_COMPONENT_NAME = 'SetDataBatch'

export const ContractCreateComponent: TransactionComponent = {
  component: 'ContractCreate',
}

export enum DefaultTxComponents {
  VALUE = 'DefaultValue',
  EXECUTION = 'DefaultExecution',
  SET_DATA = 'DefaultSetDataKey',
}

export const PermissionsSetDataKey =
  ERC725YDataKeys.LSP6['AddressPermissions:Permissions']

export { default as SetDataComponentsSelectors } from '@/transactionConfirmationSelectors/setDataComponentsSelectors'
